import type { DocumentByPathResult } from './view-document';

import { useSuspenseQuery } from '@apollo/client/react';
import {
  Box,
  Button,
  Heading,
  HStack,
  Link,
  Table,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Suspense, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { DocumentAttachFileModal } from './document-attach-file-modal';

import { GET_UPLOADS_FOR_DOCUMENT } from '#src/graphql/queries/document-uploads';

/** Keys are `{userId}/{uuid}{ext}`; list view omits the user prefix. */
function resourceIdWithoutUserPrefix(resourceId: string): string {
  const i = resourceId.indexOf('/');
  return i >= 0 ? resourceId.slice(i + 1) : resourceId;
}

function DocumentFilesUploadsTable({
  documentId,
  apiHost,
  userEmail,
}: {
  documentId: string;
  apiHost: string;
  userEmail: string;
}) {
  const { data, refetch } = useSuspenseQuery(GET_UPLOADS_FOR_DOCUMENT, {
    variables: { documentId, limit: 200 },
    fetchPolicy: 'cache-first',
  });
  const [uploadOpen, setUploadOpen] = useState(false);

  const rows = data?.uploadsForDocument ?? [];

  return (
    <VStack gap={4} align="stretch" width="100%">
      <HStack gap={2} flexWrap="wrap">
        <Button size="sm" onClick={() => void refetch()}>
          Refresh
        </Button>
        <Button
          size="sm"
          colorScheme="blue"
          onClick={() => setUploadOpen(true)}
        >
          Upload file…
        </Button>
      </HStack>
      <DocumentAttachFileModal
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        apiHost={apiHost}
        userEmail={userEmail}
        documentId={documentId}
        onUploaded={() => void refetch()}
      />
      {rows.length === 0 ? (
        <Text color="fg.muted" fontSize="sm">
          No files linked to this document yet. Use Upload file… above or paste
          images into the editor.
        </Text>
      ) : (
        <Box overflowX="auto">
          <Table.Root
            size="sm"
            variant="line"
            css={{
              '& thead tr, & tbody tr': {
                backgroundColor: 'var(--input-base-background-color)',
              },
            }}
          >
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>Created</Table.ColumnHeader>
                <Table.ColumnHeader>Filename</Table.ColumnHeader>
                <Table.ColumnHeader>Resource id</Table.ColumnHeader>
                <Table.ColumnHeader textAlign="end"> </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {rows.map((row) => (
                <Table.Row key={row.id}>
                  <Table.Cell whiteSpace="nowrap">
                    {new Date(row.createdAt).toLocaleString()}
                  </Table.Cell>
                  <Table.Cell>{row.filename ?? '—'}</Table.Cell>
                  <Table.Cell fontFamily="mono" fontSize="xs">
                    {resourceIdWithoutUserPrefix(row.resourceId)}
                  </Table.Cell>
                  <Table.Cell textAlign="end">
                    <Link
                      href={`${apiHost}/api/upload/${row.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      color="blue.600"
                    >
                      Open
                    </Link>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Box>
      )}
    </VStack>
  );
}

export function DocumentFilesPage({
  documentResult,
  requestedPath,
  apiHost,
  userEmail,
}: {
  documentResult: DocumentByPathResult;
  requestedPath: string;
  apiHost: string;
  userEmail: string;
}) {
  const doc = documentResult?.__typename === 'Document' ? documentResult : null;

  if (!doc) {
    return (
      <Box width="100%" p={4} maxW="2xl" mx="auto">
        <VStack gap={3} align="stretch">
          <Heading size="md" color="fg">
            Files
          </Heading>
          <Text color="fg.muted" fontSize="sm">
            No document at “{requestedPath}”. Create the document first, then
            upload files from the editor.
          </Text>
          <Link
            asChild
            color="blue.600"
            fontSize="sm"
            _hover={{ textDecoration: 'underline' }}
          >
            <RouterLink to={`/documents/${requestedPath}`}>
              Open document path
            </RouterLink>
          </Link>
        </VStack>
      </Box>
    );
  }

  return (
    <Box width="100%" p={4} maxW="4xl" mx="auto">
      <VStack gap={4} align="stretch">
        <VStack gap={1} align="stretch">
          <Heading size="lg" color="fg">
            Files
          </Heading>
          <Text color="fg.muted" fontSize="sm">
            Uploads linked to this document (newest first).
          </Text>
          <Link
            asChild
            color="blue.600"
            fontSize="sm"
            _hover={{ textDecoration: 'underline' }}
          >
            <RouterLink to={`/documents/${doc.path}`}>
              Back to document
            </RouterLink>
          </Link>
        </VStack>
        <Suspense fallback={<Text color="fg.muted">Loading uploads…</Text>}>
          <DocumentFilesUploadsTable
            documentId={doc.id}
            apiHost={apiHost}
            userEmail={userEmail}
          />
        </Suspense>
      </VStack>
    </Box>
  );
}
